// Produktseite (Schenk): Variantenwahl, Preis pro Paar, Galerie und In-den-Warenkorb.
// Nutzt das Shopify-Standardereignis, damit Horizons Warenkorb-Drawer und Zähler aktualisiert werden.
import { CartLinesUpdateEvent } from '@shopify/events';

const formatMoney = (cents, format) => {
  const value = (cents / 100).toFixed(2);
  const [whole, decimals] = value.split('.');
  const withSep = (sep) => whole.replace(/\B(?=(\d{3})+(?!\d))/g, sep);
  const amounts = {
    amount: `${withSep(',')}.${decimals}`,
    amount_no_decimals: withSep(','),
    amount_with_comma_separator: `${withSep('.')},${decimals}`,
    amount_no_decimals_with_comma_separator: withSep('.'),
    amount_with_apostrophe_separator: `${withSep("'")}.${decimals}`,
  };
  return format.replace(/\{\{\s*(\w+)\s*\}\}/, (_, key) => amounts[key] ?? amounts.amount);
};

const pairsIn = (variant) => {
  for (const option of variant?.options ?? []) {
    const match = /(\d+)\s*Paar/i.exec(option);
    if (match) return Number(match[1]);
  }
  return null;
};

class SsProduct extends HTMLElement {
  connectedCallback() {
    this.variants = JSON.parse(this.querySelector('[data-variants]').textContent);
    this.moneyFormat = this.dataset.moneyFormat || '{{amount_with_comma_separator}} €';
    this.form = this.querySelector('form[action*="/cart/add"]');
    this.groups = [...this.querySelectorAll('[data-option-group]')];

    this.addEventListener('change', (event) => {
      if (event.target.matches('[data-option]')) this.update(true);
    });
    this.form?.addEventListener('submit', (event) => this.addToCart(event));

    this.querySelectorAll('.ss-thumbs button').forEach((button) =>
      button.addEventListener('click', () => this.showMedia(button.dataset.mediaId))
    );
    this.querySelectorAll('[data-qty]').forEach((button) =>
      button.addEventListener('click', () => {
        const input = this.form.querySelector('input[name="quantity"]');
        input.value = Math.max(1, (Number(input.value) || 1) + Number(button.dataset.qty));
      })
    );

    this.update(false);
  }

  get selection() {
    return this.groups.map((group) => group.querySelector('input:checked')?.value);
  }

  findVariant(selection) {
    return this.variants.find((variant) => variant.options.every((value, i) => value === selection[i]));
  }

  update(fromUser) {
    const selection = this.selection;
    const variant = this.findVariant(selection);
    this.variant = variant;

    // Pro Optionswert: gibt es mit den übrigen Auswahlen eine verfügbare Variante?
    this.groups.forEach((group, index) => {
      group.querySelectorAll('input[data-option]').forEach((input) => {
        const candidate = this.findVariant(selection.map((value, i) => (i === index ? input.value : value)));
        const label = input.closest('label');
        const unavailable = !candidate || !candidate.available;
        label.classList.toggle('is-disabled', unavailable);
        input.setAttribute('aria-disabled', unavailable);
        const packPrice = label.querySelector('[data-pack-price]');
        if (packPrice) packPrice.textContent = candidate ? formatMoney(candidate.price, this.moneyFormat) : 'nicht verfügbar';
      });
      const selectedLabel = group.querySelector('[data-selected-label]');
      if (selectedLabel) selectedLabel.textContent = selection[index] ?? '';
    });

    const available = Boolean(variant?.available);
    const priceText = variant ? formatMoney(variant.price, this.moneyFormat) : '';
    this.querySelector('[data-price]').textContent = priceText || 'Nicht verfügbar';

    const pairs = pairsIn(variant);
    this.querySelector('[data-per-pair]').textContent =
      variant && pairs > 1 ? `${formatMoney(Math.round(variant.price / pairs), this.moneyFormat)} pro Paar · ` : '';

    const stock = this.querySelector('[data-stock]');
    stock.toggleAttribute('data-soldout', !available);
    if (fromUser) stock.textContent = available ? stock.dataset.inStockText || 'Auf Lager' : variant ? 'Ausverkauft' : 'Diese Kombination gibt es nicht';
    else stock.dataset.inStockText = available ? stock.textContent : 'Auf Lager';

    this.querySelectorAll('[data-add]').forEach((button) => {
      button.disabled = !available;
    });
    this.querySelector('.ss-buyrow [data-add]').textContent = available ? 'In den Warenkorb' : 'Ausverkauft';
    const mobileLabel = this.querySelector('[data-mobile-label]');
    if (mobileLabel) mobileLabel.textContent = available ? `In den Warenkorb · ${priceText}` : 'Ausverkauft';

    if (!variant) return;
    this.form.querySelector('input[name="id"]').value = variant.id;
    if (variant.featured_media) this.showMedia(String(variant.featured_media.id));
    if (fromUser) {
      const url = new URL(window.location.href);
      url.searchParams.set('variant', variant.id);
      window.history.replaceState({}, '', url);
    }
  }

  showMedia(mediaId) {
    const images = this.querySelectorAll('.ss-gallery__main img');
    if (![...images].some((img) => img.dataset.mediaId === mediaId)) return;
    images.forEach((img) => {
      const active = img.dataset.mediaId === mediaId;
      img.hidden = !active;
      if (active) img.loading = 'eager';
    });
    this.querySelectorAll('.ss-thumbs button').forEach((button) =>
      button.setAttribute('aria-current', button.dataset.mediaId === mediaId)
    );
  }

  async addToCart(event) {
    event.preventDefault();
    if (!this.variant?.available) return;

    const buttons = this.querySelectorAll('[data-add]');
    const message = this.querySelector('[data-add-msg]');
    const formData = new FormData(this.form);
    const quantity = Number(formData.get('quantity')) || 1;
    const sectionIds = [...document.querySelectorAll('cart-items-component')]
      .map((el) => el.dataset.sectionId)
      .filter(Boolean);
    if (sectionIds.length) formData.append('sections', sectionIds.join(','));

    buttons.forEach((button) => (button.disabled = true));
    message.textContent = '';

    const deferred = CartLinesUpdateEvent.createPromise();
    this.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        context: 'product',
        lines: [{ merchandiseId: String(formData.get('id')), quantity }],
        promise: deferred.promise,
      })
    );

    try {
      const response = await fetch(Theme.routes.cart_add_url, {
        method: 'POST',
        headers: { Accept: 'application/javascript', 'X-Requested-With': 'XMLHttpRequest' },
        body: formData,
      }).then((res) => res.json());

      if (response.status) throw new Error(response.description || response.message || 'Fehler');

      const cart = await fetch(`${Theme.routes.cart_url}.js`).then((res) => res.json());
      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: {
          items: cart.items,
          source: 'ss-product',
          itemCount: quantity,
          productId: this.dataset.productId,
          sections: response.sections,
          didError: false,
        },
      });
      message.textContent = 'Zum Warenkorb hinzugefügt.';
    } catch (error) {
      deferred.reject(error);
      message.textContent = `Konnte nicht hinzugefügt werden: ${error.message}`;
    } finally {
      buttons.forEach((button) => (button.disabled = !this.variant?.available));
    }
  }
}

if (!customElements.get('ss-product')) customElements.define('ss-product', SsProduct);
